import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown,
  Loader2,
  RefreshCw,
  Search,
  Save,
  Users,
  Settings2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SmartPagination from '@/components/SmartPagination';
import { admin } from '@/lib/api';
import { toast } from 'sonner';
import { getMembershipTierLabel, getMembershipTierStyle } from '@/lib/membership-utils';

const PAGE_SIZE = 20;
const TIER_KEYS = ['bronze', 'silver', 'gold'];

const STATUS_LABELS = {
  active: 'Aktif',
  cancelled: 'İptal',
  expired: 'Süresi dolmuş',
  past_due: 'Ödeme gecikmiş',
};

function formatDateTimeTr(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function linesToFeatures(text) {
  return (text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function featuresToLines(list) {
  return Array.isArray(list) ? list.join('\n') : '';
}

const emptyTierForm = () => ({
  name: '',
  name_tr: '',
  price_monthly: '',
  price_yearly: '',
  shipping_discount: '',
  points_multiplier: '',
  early_access_days: '',
  features: '',
  features_en: '',
});

const MembershipManagementTab = () => {
  const [subTab, setSubTab] = useState('members');
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [usdTryRate, setUsdTryRate] = useState('35');
  const [tierForms, setTierForms] = useState({
    bronze: emptyTierForm(),
    silver: emptyTierForm(),
    gold: emptyTierForm(),
  });
  const [settingsMeta, setSettingsMeta] = useState({ updated_at: null, updated_by: null });

  const fetchMembers = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const params = { page, limit: PAGE_SIZE };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tierFilter !== 'all') params.tier = tierFilter;
      const res = await admin.membershipsList(params);
      setRows(res.data.subscriptions || []);
      setTotal(res.data.total || 0);
      setActiveCount(res.data.active_count ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Üyelikler yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoadingMembers(false);
    }
  }, [page, search, statusFilter, tierFilter]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      const res = await admin.membershipsGetSettings();
      const tiers = res.data.tiers || {};
      setUsdTryRate(String(res.data.usd_try_rate ?? 35));
      setSettingsMeta({
        updated_at: res.data.updated_at,
        updated_by: res.data.updated_by,
      });
      const forms = {};
      TIER_KEYS.forEach((key) => {
        const t = tiers[key] || {};
        forms[key] = {
          name: t.name || '',
          name_tr: t.name_tr || '',
          price_monthly: t.price_monthly ?? '',
          price_yearly: t.price_yearly ?? '',
          shipping_discount: t.shipping_discount ?? '',
          points_multiplier: t.points_multiplier ?? '',
          early_access_days: t.early_access_days ?? '',
          features: featuresToLines(t.features),
          features_en: featuresToLines(t.features_en),
        };
      });
      setTierForms(forms);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Üyelik ayarları yüklenemedi');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'members') fetchMembers();
  }, [subTab, fetchMembers]);

  useEffect(() => {
    if (subTab === 'settings') fetchSettings();
  }, [subTab, fetchSettings]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const updateTierField = (tierKey, field, value) => {
    setTierForms((prev) => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], [field]: value },
    }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const tiers = {};
      TIER_KEYS.forEach((key) => {
        const f = tierForms[key];
        tiers[key] = {
          name: f.name.trim(),
          name_tr: f.name_tr.trim(),
          price_monthly: parseFloat(f.price_monthly) || 0,
          price_yearly: parseFloat(f.price_yearly) || 0,
          shipping_discount: parseInt(f.shipping_discount, 10) || 0,
          points_multiplier: parseFloat(f.points_multiplier) || 1,
          early_access_days: parseInt(f.early_access_days, 10) || 0,
          features: linesToFeatures(f.features),
          features_en: linesToFeatures(f.features_en),
        };
      });
      const res = await admin.membershipsUpdateSettings({
        tiers,
        usd_try_rate: parseFloat(usdTryRate) || 35,
      });
      toast.success(res.data.message || 'Ayarlar kaydedildi');
      setSettingsMeta({
        updated_at: res.data.updated_at,
        updated_by: res.data.updated_by,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSavingSettings(false);
    }
  };

  const tryPreview = (usd, rate) => {
    const n = parseFloat(usd);
    const r = parseFloat(rate);
    if (!n || !r) return '—';
    return `₺${Math.round(n * r).toLocaleString('tr-TR')}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <Crown className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Üyelikler</h1>
          <p className="text-sm text-muted-foreground">
            Üye olan kullanıcıları görüntüleyin ve paket fiyatlarını yönetin
          </p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="members" className="gap-2">
            <Users className="w-4 h-4" />
            Üye Listesi
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings2 className="w-4 h-4" />
            Paket Ayarları
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Toplam kayıt</CardDescription>
                <CardTitle className="text-2xl">{total}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Aktif üyelik</CardDescription>
                <CardTitle className="text-2xl text-green-700">{activeCount}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Üyelik Abonelikleri</CardTitle>
              <CardDescription>Ziraat ile ödeme yapan ve üye olan kullanıcılar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="E-posta, isim veya işlem no..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm durumlar</SelectItem>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="cancelled">İptal</SelectItem>
                    <SelectItem value="expired">Süresi dolmuş</SelectItem>
                    <SelectItem value="past_due">Ödeme gecikmiş</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Paket" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm paketler</SelectItem>
                    <SelectItem value="bronze">Bronz</SelectItem>
                    <SelectItem value="silver">Gümüş</SelectItem>
                    <SelectItem value="gold">Altın</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" variant="secondary">Ara</Button>
                <Button type="button" variant="outline" onClick={fetchMembers}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </form>

              {loadingMembers ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead>Paket</TableHead>
                        <TableHead>Dönem</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Bitiş</TableHead>
                        <TableHead>Ödeme</TableHead>
                        <TableHead>Kayıt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            Kayıt bulunamadı
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <div className="font-medium">{row.user_name || '—'}</div>
                              <div className="text-xs text-muted-foreground">{row.user_email || row.user_id}</div>
                            </TableCell>
                            <TableCell>
                              <Badge className={getMembershipTierStyle(row.tier)}>
                                {getMembershipTierLabel(row.tier, 'tr')}
                              </Badge>
                            </TableCell>
                            <TableCell>{row.billing_period === 'yearly' ? 'Yıllık' : 'Aylık'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{STATUS_LABELS[row.status] || row.status}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">{formatDateTimeTr(row.current_period_end)}</TableCell>
                            <TableCell className="text-xs font-mono">{row.payment_ref || '—'}</TableCell>
                            <TableCell className="text-sm">{formatDateTimeTr(row.created_at)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <SmartPagination page={page} totalPages={totalPages} onPageChange={setPage} />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4 space-y-4">
          {loadingSettings ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Genel Ayarlar</CardTitle>
                  <CardDescription>
                    USD fiyatları TRY&apos;ye çevrilir. Değişiklikler anında sitede yansır.
                  </CardDescription>
                </CardHeader>
                <CardContent className="max-w-xs">
                  <Label htmlFor="usd_try_rate">USD → TRY kuru</Label>
                  <Input
                    id="usd_try_rate"
                    type="number"
                    min="1"
                    step="0.01"
                    value={usdTryRate}
                    onChange={(e) => setUsdTryRate(e.target.value)}
                    className="mt-1"
                  />
                  {settingsMeta.updated_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Son güncelleme: {formatDateTimeTr(settingsMeta.updated_at)}
                      {settingsMeta.updated_by ? ` — ${settingsMeta.updated_by}` : ''}
                    </p>
                  )}
                </CardContent>
              </Card>

              {TIER_KEYS.map((tierKey) => {
                const f = tierForms[tierKey];
                return (
                  <Card key={tierKey}>
                    <CardHeader>
                      <CardTitle className="capitalize">{getMembershipTierLabel(tierKey, 'tr')} Paketi</CardTitle>
                      <CardDescription>
                        Aylık TRY: {tryPreview(f.price_monthly, usdTryRate)} · Yıllık TRY: {tryPreview(f.price_yearly, usdTryRate)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>İsim (EN)</Label>
                        <Input value={f.name} onChange={(e) => updateTierField(tierKey, 'name', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>İsim (TR)</Label>
                        <Input value={f.name_tr} onChange={(e) => updateTierField(tierKey, 'name_tr', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Aylık fiyat (USD)</Label>
                        <Input type="number" min="0" step="0.01" value={f.price_monthly} onChange={(e) => updateTierField(tierKey, 'price_monthly', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Yıllık fiyat (USD)</Label>
                        <Input type="number" min="0" step="0.01" value={f.price_yearly} onChange={(e) => updateTierField(tierKey, 'price_yearly', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Kargo indirimi (%)</Label>
                        <Input type="number" min="0" max="100" value={f.shipping_discount} onChange={(e) => updateTierField(tierKey, 'shipping_discount', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Puan çarpanı</Label>
                        <Input type="number" min="0" step="0.1" value={f.points_multiplier} onChange={(e) => updateTierField(tierKey, 'points_multiplier', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Erken erişim (gün)</Label>
                        <Input type="number" min="0" value={f.early_access_days} onChange={(e) => updateTierField(tierKey, 'early_access_days', e.target.value)} className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Özellikler (TR) — her satır bir madde</Label>
                        <Textarea rows={4} value={f.features} onChange={(e) => updateTierField(tierKey, 'features', e.target.value)} className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Özellikler (EN) — her satır bir madde</Label>
                        <Textarea rows={4} value={f.features_en} onChange={(e) => updateTierField(tierKey, 'features_en', e.target.value)} className="mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <div className="flex justify-end">
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="gap-2">
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Ayarları Kaydet
                </Button>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MembershipManagementTab;
